import { useEffect, useState } from "react";
import { Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, Moon, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { lsGet, lsSet } from "../lib/safeStorage";
import { useLangUnit, fmtTemp, iconForCondition, translateWeatherDesc } from "../lib/weather";
import { useI18n } from "../lib/i18n";
import WeatherForecastModal from "./WeatherForecastModal";

const CACHE_KEY = "habano.weather.v1";
const ICONS = { Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, Moon };

export default function WeatherWidget() {
  const { lang } = useI18n();
  const [data, setData] = useState(() => {
    try { return JSON.parse(lsGet(CACHE_KEY) || "null"); } catch { return null; }
  });
  const [open, setOpen] = useState(false);
  const [unit] = useLangUnit();
  // Kept for the forecast modal API which still exposes a toggle;
  // iter155 makes it a no-op since the widget follows language now.
  const toggle = () => {};

  useEffect(() => {
    api.get("/weather").then(({ data: d }) => {
      if (d && d.available && d.current) {
        setData(d);
        try { lsSet(CACHE_KEY, JSON.stringify(d)); } catch { /* ignore */ }
      }
    }).catch(() => { /* keep cache */ });
  }, []);

  // If no data → hide widget entirely
  if (!data || !data.available || !data.current) return null;
  const c = data.current;
  const IconCmp = ICONS[iconForCondition(c.main, c.icon)] || Sun;

  return (
    <>
      <button
        type="button"
        data-testid="weather-widget"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 select-none justify-center shrink-0"
        title={`Havana · ${translateWeatherDesc(c.description, lang) || ""}`}
        style={{
          background: "#1D1D1D",
          color: "#E8B84B",
          fontFamily: "var(--font-sans, 'DM Sans')",
          fontSize: 13,
          padding: "6px 12px",
          border: "1px solid rgba(232,184,75,0.28)",
          borderRadius: 9999,
          height: 32,
          maxHeight: 36,
          minWidth: 120,
        }}
      >
        <IconCmp className="w-3.5 h-3.5 shrink-0" strokeWidth={1.7} />
        <span
          data-testid="weather-temp"
          className="font-mono"
          style={{ minWidth: 38, textAlign: "center", display: "inline-block" }}
        >
          {fmtTemp(c.temp_f, unit)}
        </span>
        <ChevronDown
          data-testid="weather-chevron"
          className="w-3 h-3 opacity-80 shrink-0"
          strokeWidth={1.6}
          aria-hidden="true"
        />
      </button>
      <WeatherForecastModal open={open} onOpenChange={setOpen} weather={data} unit={unit} toggleUnit={toggle} />
    </>
  );
}
